import { useEffect, useState } from 'react'
import { useAppSelector } from '@/app/hooks'
import { selectAuth } from '@/features/auth/authSlice'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { useNotificationCounts } from '@/hooks/useNotificationCounts'
import { getUserInitials } from '@/features/invitee/utils/userDrawerHelpers'
import { UserDrawer } from './UserDrawer'
import type { UserMenuButtonProps } from '../types/components'

/**
 * Profile trigger + notification badge + drawer (Angular layout page-header profile).
 */
export function UserMenuButton({
  testIdPrefix = 'user-menu',
  className = '',
}: UserMenuButtonProps) {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [avatarBroken, setAvatarBroken] = useState(false)
  const { user } = useAppSelector(selectAuth)
  const { counts, todoPrompt, dismissTodoPrompt, refreshCounts } =
    useNotificationCounts(true)

  const totalBadge = counts?.total ?? 0
  const initials = getUserInitials(user?.first_name, user?.last_name)

  useEffect(() => {
    setAvatarBroken(false)
  }, [user?.profile_pic])

  return (
    <>
      <div className={`relative ${className}`}>
        <button
          type="button"
          data-testid={`${testIdPrefix}-user-menu-button`}
          aria-label="Open user menu"
          className="relative flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-[rgb(21_151_255)]/20 bg-teal-600 text-white shadow-sm transition-colors select-none hover:border-[rgb(21_151_255)]/35 hover:bg-teal-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(21_151_255)] focus-visible:ring-offset-1 sm:h-9 sm:w-9"
          onClick={() => setIsDrawerOpen(true)}
        >
          {user?.profile_pic && !avatarBroken ? (
            <img
              src={user.profile_pic}
              alt="profile picture"
              className="h-full w-full object-cover"
              onError={() => setAvatarBroken(true)}
            />
          ) : (
            <span className="text-[11px] font-bold leading-none sm:text-xs" aria-hidden>
              {initials}
            </span>
          )}
        </button>
        {totalBadge > 0 ? (
          <span
            data-testid={`${testIdPrefix}-notification-badge`}
            className="absolute -top-1 -right-1 flex h-5 min-w-5 items-center justify-center rounded-full border-2 border-white bg-red-500 px-1 text-[10px] font-bold text-white"
          >
            {totalBadge}
          </span>
        ) : null}
      </div>

      <UserDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onCountsRefresh={refreshCounts}
      />

      {todoPrompt?.header && todoPrompt.description ? (
        <ConfirmDialog
          isOpen
          title={todoPrompt.header}
          message={todoPrompt.description}
          primaryLabel="Ok"
          onPrimary={dismissTodoPrompt}
          secondaryLabel=""
          onSecondary={dismissTodoPrompt}
          onDismiss={dismissTodoPrompt}
        />
      ) : null}
    </>
  )
}
